import { useMemo } from "react";

import { ProtectedLink } from "@impero-com/common/components/protectedLink";
import { TFunction, useTranslation } from "@impero-com/common/utils/i18n";
import { assertNever } from "@impero-com/common/utils/obj";
import {
    TST_BLESSED_ID_COLUMN_SELECTION,
    TST_COLUMN_SIZE,
    TST_DEFAULT_PAGE_SIZE,
} from "@impero-com/ui";
import {
    usePresetTableSearchParams,
    ZodCodecMapToType,
} from "@impero-com/ui/domain";
import * as Sentry from "@sentry/react";
import {
    Column,
    createColumnHelper,
    getCoreRowModel,
    getFacetedUniqueValues,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    useReactTable,
} from "@tanstack/react-table";
import { Checkbox } from "antd";

import {
    SavedReportForUserListDto,
    SavedReportType2,
    StandardAccessRight,
} from "api/generated/model";
import { CustomEllipsisLabel } from "components/table/tsTable/CustomEllipsisLabel";
import { TSTValidMultipleOptionFilterSelect } from "components/table/tsTable/TSTValidMultipleOptionFilterSelect";
import { LegacyRoute } from "legacyRoutes";
import { formatAccessRight } from "utils/accessRights";
import { canViewSavedReport } from "utils/actions";
import { createFilterFnWithMode } from "utils/table/createFilterFnWithMode";
import { createFilterHelpersGetter } from "utils/table/getTypedFilterValue";
import { tstArrIncludesSomeFilterFn } from "utils/table/tstArrIncludesSomeFilterFn";

import { formatSavedReportType } from "../utils";

import {
    SAVED_REPORTS_LIST_COLUMN_IDS,
    SAVED_REPORTS_TABLE_STATE_STORAGE_KEY,
    savedReportsListColumnFiltersCodecMap,
    savedReportsListTableDefaultValues,
} from "./consts";

export interface SavedReportRow {
    id: string;
    name: string;
    description: string;
    type: SavedReportType2;
    typeLabel: string;
    accessRight: StandardAccessRight;
    accessRightLabel: string;
    canView: boolean;
}

export function useUserSavedReportsListTable(data: SavedReportForUserListDto) {
    const { t } = useTranslation();

    // Rebuild rows on language changes to invalidate TanStack's cached accessor values.
    const tableData = useMemo<SavedReportRow[]>(
        () =>
            data.content.map(
                ({ resource: { savedReport, accessRight }, actions }) => ({
                    id: savedReport.id,
                    name: savedReport.title,
                    description: savedReport.description ?? "",
                    type: savedReport.type,
                    typeLabel: formatSavedReportType(savedReport.type, t),
                    accessRight,
                    accessRightLabel: formatAccessRight(accessRight),
                    canView: canViewSavedReport(actions),
                })
            ),
        [data, t]
    );

    const stateAndOnChanges = usePresetTableSearchParams(
        SAVED_REPORTS_TABLE_STATE_STORAGE_KEY,
        savedReportsListTableDefaultValues,
        savedReportsListColumnFiltersCodecMap,
        t("Table_RemovedFiltersWarning"),
        Sentry.captureException
    );

    const table = useReactTable({
        ...stateAndOnChanges,
        data: tableData,
        columns: buildColumns(t),
        getRowId: (row) => row.id,
        initialState: {
            ...stateAndOnChanges.initialState,
            pagination: { pageSize: TST_DEFAULT_PAGE_SIZE, pageIndex: 0 },
        },
        enableSortingRemoval: false,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFacetedUniqueValues: getFacetedUniqueValues(),
        getPaginationRowModel: getPaginationRowModel(),
    });

    return {
        table,
        tableId: SAVED_REPORTS_TABLE_STATE_STORAGE_KEY,
        getFilterComponent: (column: Column<SavedReportRow>) =>
            getFilterComponent(t, column),
    };
}

const columnHelper = createColumnHelper<SavedReportRow>();

function buildColumns(t: TFunction) {
    return [
        columnHelper.display({
            id: TST_BLESSED_ID_COLUMN_SELECTION,
            header: ({ table }) => (
                <Checkbox
                    checked={table.getIsAllPageRowsSelected()}
                    indeterminate={table.getIsSomePageRowsSelected()}
                    onChange={table.getToggleAllRowsSelectedHandler()}
                />
            ),
            cell: ({ row }) => (
                <Checkbox
                    checked={row.getIsSelected()}
                    onChange={row.getToggleSelectedHandler()}
                />
            ),
            size: TST_COLUMN_SIZE.CHECKBOX,
            meta: { fixedWidth: true },
        }),
        columnHelper.accessor((row) => row.name, {
            id: SAVED_REPORTS_LIST_COLUMN_IDS.NAME,
            header: () => t("Report_Details_Name"),
            cell: ({ row: { original: row } }) => (
                <ProtectedLink
                    href={`${LegacyRoute.REPORT_CONTROL_RESULT_STATUS_SAVED}/${row.id}`}
                    hasAccess={row.canView}
                >
                    {row.name}
                </ProtectedLink>
            ),
            enableSorting: true,
            sortingFn: "alphanumericCaseSensitive",
            enableColumnFilter: true,
            filterFn: createFilterFnWithMode<SavedReportRow, string[]>(
                (row, value) =>
                    tstArrIncludesSomeFilterFn(
                        row,
                        SAVED_REPORTS_LIST_COLUMN_IDS.NAME,
                        value
                    )
            ),
            size: TST_COLUMN_SIZE.L,
        }),
        columnHelper.accessor((row) => row.description, {
            id: SAVED_REPORTS_LIST_COLUMN_IDS.DESCRIPTION,
            header: () => t("Report_SavedReports_Column_Description"),
            cell: (info) => <span>{info.getValue()}</span>,
            enableSorting: true,
            sortingFn: "alphanumericCaseSensitive",
            enableColumnFilter: false,
            size: TST_COLUMN_SIZE.XL,
        }),
        columnHelper.accessor((row) => row.typeLabel, {
            id: SAVED_REPORTS_LIST_COLUMN_IDS.TYPE,
            header: () => t("Report_SavedReports_Column_Type"),
            cell: (info) => <span>{info.getValue()}</span>,
            enableSorting: true,
            sortingFn: "alphanumeric",
            enableColumnFilter: true,
            // (typeLabel) is derived from the underlying value (type).
            // The filter needs to operate on the underlying value to ensure correct filtering behavior.
            filterFn: createFilterFnWithMode<SavedReportRow, string[]>(
                (row, value) => value.includes(row.original.type)
            ),
            size: TST_COLUMN_SIZE.XL,
        }),
        columnHelper.accessor((row) => row.accessRightLabel, {
            id: SAVED_REPORTS_LIST_COLUMN_IDS.ACCESS_RIGHT,
            header: () => t("Column_Access"),
            cell: (info) => <span>{info.getValue()}</span>,
            enableSorting: true,
            sortingFn: "alphanumeric",
            enableColumnFilter: true,
            filterFn: createFilterFnWithMode<SavedReportRow, string[]>(
                (row, value) => value.includes(row.original.accessRight)
            ),
            size: TST_COLUMN_SIZE.XL,
        }),
    ];
}

type SavedReportsListColumnFilters = ZodCodecMapToType<
    typeof savedReportsListColumnFiltersCodecMap
>;

function getFilterComponent(t: TFunction, column: Column<SavedReportRow>) {
    const getFilterHelpers =
        createFilterHelpersGetter<SavedReportsListColumnFilters>(column);
    const columnId = column.id as keyof SavedReportsListColumnFilters;

    switch (columnId) {
        case SAVED_REPORTS_LIST_COLUMN_IDS.NAME: {
            const { value, setFilterValue } = getFilterHelpers(columnId);
            const values: Map<string, number> = column.getFacetedUniqueValues();
            return (
                <TSTValidMultipleOptionFilterSelect
                    column={column}
                    value={value}
                    setFilterValue={setFilterValue}
                    options={[...values.keys()].map((name) => ({
                        label: name,
                        value: name,
                        customLabel: <CustomEllipsisLabel text={name} />,
                    }))}
                    placeholder={t("Report_Details_Name")}
                />
            );
        }
        case SAVED_REPORTS_LIST_COLUMN_IDS.TYPE: {
            const { value, setFilterValue } = getFilterHelpers(columnId);
            const types = new Set(
                column
                    .getFacetedRowModel()
                    .flatRows.map((row) => row.original.type)
            );
            return (
                <TSTValidMultipleOptionFilterSelect
                    column={column}
                    value={value}
                    setFilterValue={setFilterValue}
                    options={[...types].map((type) => ({
                        label: formatSavedReportType(type, t),
                        value: type,
                    }))}
                    placeholder={t("Report_SavedReports_Column_Type")}
                />
            );
        }
        case SAVED_REPORTS_LIST_COLUMN_IDS.ACCESS_RIGHT: {
            const { value, setFilterValue } = getFilterHelpers(columnId);
            const accessRights = new Set(
                column
                    .getFacetedRowModel()
                    .flatRows.map((row) => row.original.accessRight)
            );
            return (
                <TSTValidMultipleOptionFilterSelect
                    column={column}
                    value={value}
                    setFilterValue={setFilterValue}
                    options={[...accessRights].map((right) => ({
                        label: formatAccessRight(right),
                        value: right,
                    }))}
                    placeholder={t("Column_Access")}
                />
            );
        }
        default:
            return assertNever(columnId);
    }
}
